import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { API_BASE_URL } from '../../config/api';
import './HomeSplashPopup.css';

const STORAGE_KEY = 'digirasa_web_splash';

function mediaUrl(path) {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function bannerSignature(banners) {
  return banners.map((item) => item.id).join(',');
}

function shouldShowSplash(banners) {
  if (!banners.length) return false;
  const frequency = banners[0].display_frequency || 'once_per_day';
  const sig = bannerSignature(banners);
  if (frequency === 'always') return true;
  if (frequency === 'once_per_session') {
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== sig;
    } catch {
      return true;
    }
  }
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!raw || raw.sig !== sig) return true;
    const today = new Date().toISOString().slice(0, 10);
    return raw.day !== today;
  } catch {
    return true;
  }
}

function markSplashSeen(banners) {
  const sig = bannerSignature(banners);
  const frequency = banners[0]?.display_frequency || 'once_per_day';
  try {
    sessionStorage.setItem(STORAGE_KEY, sig);
  } catch {
    /* ignore */
  }
  if (frequency === 'once_per_session') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ sig, day: new Date().toISOString().slice(0, 10) }),
    );
  } catch {
    /* ignore */
  }
}

const HomeSplashPopup = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`${API_BASE_URL}/api/banners/active`);
        const list = Array.isArray(data?.banners) && data.banners.length
          ? data.banners
          : (data?.data ? [data.data] : []);
        if (cancelled || !list.length || !shouldShowSplash(list)) return;
        setBanners(list);
        setOpen(true);
      } catch {
        /* no splash if banners cannot load */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const current = banners[index] || null;
  const durationMs = useMemo(() => {
    const seconds = Number(current?.display_duration_seconds);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
  }, [current]);

  const close = useCallback(() => {
    markSplashSeen(banners);
    setOpen(false);
  }, [banners]);

  const goTo = useCallback((nextIndex) => {
    if (!banners.length) return;
    setIndex((nextIndex + banners.length) % banners.length);
  }, [banners.length]);

  useEffect(() => {
    if (!open || banners.length < 2 || current?.media_type === 'video') return undefined;
    const timer = window.setTimeout(() => goTo(index + 1), durationMs);
    return () => window.clearTimeout(timer);
  }, [open, banners.length, current, durationMs, goTo, index]);

  const handleAction = () => {
    if (!current) return;
    const type = current.action_type || 'none';
    const value = String(current.action_value || '').trim();
    if (type === 'url' && value) {
      if (value.startsWith('/')) {
        close();
        navigate(value);
        return;
      }
      window.open(value, '_blank', 'noopener,noreferrer');
      close();
      return;
    }
    if (type === 'deeplink' && value.startsWith('/')) {
      close();
      navigate(value);
      return;
    }
    if (type === 'promo_code' && value) {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).catch(() => {});
      }
    }
    close();
  };

  if (!open || !current) return null;

  const src = mediaUrl(current.media_url);
  const clickable = current.action_type && current.action_type !== 'none';

  return (
    <div className="home-splash" role="dialog" aria-modal="true" aria-label={current.title || 'Promotion'}>
      <button type="button" className="home-splash__backdrop" aria-label="Close promotion" onClick={close} />
      <div className="home-splash__card">
        <button type="button" className="home-splash__close" onClick={close} aria-label="Close">
          ×
        </button>
        <button
          type="button"
          className="home-splash__media"
          onClick={clickable ? handleAction : undefined}
          disabled={!clickable}
        >
          {current.media_type === 'video' ? (
            <video
              src={src}
              autoPlay
              muted={current.is_muted !== false}
              playsInline
              onEnded={() => (banners.length > 1 ? goTo(index + 1) : close())}
            />
          ) : (
            <img src={src} alt={current.title || 'Promotion'} />
          )}
        </button>
        {banners.length > 1 ? (
          <div className="home-splash__dots">
            {banners.map((item, i) => (
              <button
                key={item.id || i}
                type="button"
                className={`home-splash__dot${i === index ? ' home-splash__dot--active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Promotion ${i + 1}`}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default HomeSplashPopup;
